import React, { memo, useMemo, useEffect, lazy, Suspense } from 'react'
import { Loading } from 'components/shared'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import {
	LAYOUT_TYPE_CLASSIC,
	LAYOUT_TYPE_MODERN,
	LAYOUT_TYPE_SIMPLE,
	LAYOUT_TYPE_STACKED_SIDE,
	LAYOUT_TYPE_DECKED,
	LAYOUT_TYPE_BLANK
} from 'constants/theme.constant'
import useAuth from 'utils/hooks/useAuth'
import useDirection from 'utils/hooks/useDirection'
import useLocale from 'utils/hooks/useLocale'
import useSiteLogos from 'utils/hooks/useSiteLogos'
import useSiteFavicon from 'utils/hooks/useSiteFavicon'
import useSiteName from 'utils/hooks/useSiteName'
import useAnnouncement from 'utils/hooks/useAnnouncement'
import useFeatures from 'utils/hooks/useFeatures'

const layouts = {
	[LAYOUT_TYPE_CLASSIC]: lazy(() => import('./ClassicLayout')),
	[LAYOUT_TYPE_MODERN]: lazy(() => import('./ModernLayout')),
	[LAYOUT_TYPE_STACKED_SIDE]: lazy(() => import('./StackedSideLayout')),
	[LAYOUT_TYPE_SIMPLE]: lazy(() => import('./SimpleLayout')),
	[LAYOUT_TYPE_DECKED]: lazy(() => import('./DeckedLayout')),
	[LAYOUT_TYPE_BLANK]: lazy(() => import('./BlankLayout')),
}

const Landing = lazy(() => import('views/landing'))

const Layout = () => {

	const location = useLocation()

	const layoutType = useSelector((state) => state.theme.layout.type)

	const { authenticated, verified } = useAuth()

	useDirection()

	useLocale()

	useSiteLogos()

	useSiteFavicon()

	// Loaded here (not in AnnouncementBar) because PageContainer -- and so the
	// bar -- remounts on every route change, which would refire the GET.
	useAnnouncement()

	useFeatures()

	const { siteName } = useSiteName()

	useEffect(() => {
		document.title = siteName.toUpperCase()
	}, [siteName])

	const AppLayout = useMemo(() => {
		if (authenticated && verified) {
			return layouts[layoutType]
		}
		return lazy(() => import('./AuthLayout'))
	}, [layoutType, authenticated, verified])

	const suspenseFallback = (
		<div className="flex flex-auto flex-col h-[100vh]">
			<Loading loading={true} />
		</div>
	)

	// The public landing page (homepage) renders full-screen for everyone,
	// outside the dashboard/auth shell.
	if (location.pathname === '/') {
		return (
			<Suspense fallback={suspenseFallback}>
				<Landing />
			</Suspense>
		)
	}

	return (
		<Suspense fallback={suspenseFallback}>
			<AppLayout />
		</Suspense>
	)
}

export default memo(Layout)
